import React, { type FC, type ReactNode, type MouseEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { CustomThemeButton } from '@atlaskit/button';
import { FORGE_ENTITY_TYPE } from '@atlassian/jira-issue-view-common-constants';
import type { ActivityItem } from '@atlassian/jira-issue-view-common-types/src/activity-item-type';
import { ForgeIssueActivityItem } from '@atlassian/jira-issue-view-forge-issue-activity/src';
import {
	ContextualAnalyticsData,
	fireScreenAnalyticsDeferred,
	MountEvent,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import type { SelectActivityItem } from '../../common/ui/types';
import { buttonTheme } from '../../styled';

const ButtonComponent = ({
	children,
	onClick,
	isSelected,
	testId,
}: {
	children: ReactNode;
	onClick: (event: MouseEvent, analyticsEvent: UIAnalyticsEvent) => void;
	isSelected: boolean;
	testId?: string;
}) => (
	<CustomThemeButton
		appearance="subtle"
		spacing="compact"
		isSelected={isSelected}
		theme={buttonTheme(isSelected)}
		onClick={onClick}
		testId={testId}
		aria-checked={isSelected}
		role="menuitemradio"
		interactionName="auto-generated.jira-issue-activity-feed.ui.buttons.button"
	>
		{children}
	</CustomThemeButton>
);

type Props = {
	items: ActivityItem[];
	selectedItem: ActivityItem;
	onSelectActivityItem: SelectActivityItem;
};

export const ActivityFeedButtons: FC<Props> = ({
	items,
	selectedItem,
	onSelectActivityItem,
}: Props) => (
	<ContextualAnalyticsData sourceType={SCREEN} sourceName="activityFeedButtons">
		<MountEvent onMount={(event) => fireScreenAnalyticsDeferred(event)} />
		{items.map((item: ActivityItem) => {
			const props = {
				key: item.key,
				isSelected: selectedItem.key === item.key,

				onClick: (event: MouseEvent, analyticsEvent: UIAnalyticsEvent) =>
					onSelectActivityItem(item, analyticsEvent, 'buttonItemSelected', event.timeStamp),
			};

			return item.type === FORGE_ENTITY_TYPE ? (
				<ForgeIssueActivityItem {...props} component={ButtonComponent} item={item} />
			) : (
				<ButtonComponent {...props} testId={`issue-activity-feed.ui.buttons.${item.key}`}>
					{item.name}
				</ButtonComponent>
			);
		})}
	</ContextualAnalyticsData>
);
